import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../../components/ui';
import { SplitViewTabBar } from '../../components/generic/SplitViewWithTabs';
import SupplierDetailsTab from './components/tabs/SupplierDetailsTab';
import SupplierAuditLogTab from './components/tabs/SupplierAuditLogTab';
import { useFlexibleList } from '../../hooks/useSWR';
import { DocumentTextIcon, ClipboardCheckIcon, XIcon } from '../../components/ui/Icons';

/**
 * Custom Split View Panel for Supplier module with responsive tab carousel
 * 
 * Features:
 * - Smart Detection: Automatically calculates how many tabs can fit based on drawer width
 * - Config-Based: Uses actual drawerWidth from module config for precise calculations
 * - Clean Navigation: Only shows Previous/Next arrows when needed
 * - No Overflow: All elements stay within panel boundaries
 * - Responsive: Adapts to any drawer width configuration
 * 
 * Tabs:
 * - Details: Supplier information and file attachments
 * - Audit Log: Complete audit trail for the supplier
 */
const CustomSupplierSplitView = ({ 
  isOpen, 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields, 
  labels,
  onClose,
  onRecordUpdate // Callback to refresh Supplier data
}) => {
  const { theme } = useTheme();
  // Fetch Supplier audit logs using master entity endpoint (includes all related entities)
  const { data: auditLogs = [] } = useFlexibleList(
    selectedRecord?.id ? `/audit-log/master/${config?.features?.masterEntityTypeId || 8}/${selectedRecord.id}` : null
  );
  
  // Fetch file attachments count (if the endpoint exists)
  const { data: fileAttachments = [] } = useFlexibleList(
    selectedRecord?.id ? `/file-attachment/entity/${config?.entityTypeId || 8}/${selectedRecord.id}` : null
  );

  // Define all tabs with their properties
  const allTabs = [
    {
      value: 'details',
      label: 'Details',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      badge: fileAttachments.length,
      badgeColor: "bg-green-600"
    },
    {
      value: 'audit-log',
      label: 'Audit Log',
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: auditLogs.length,
      badgeColor: "bg-red-600"
    }
  ];

  if (!isOpen) return null;

  return (
    <div 
      className={cn("hidden lg:block min-h-0 h-full pb-2")}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div className={cn("min-h-0 h-full rounded-xl border flex flex-col", theme.border.light, theme.colors.surface, theme.shadow.md)}>
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn("text-sm font-semibold", theme.text.primary)}>
            {selectedRecord ? `Supplier - ${selectedRecord.name || selectedRecord.id}` : "Select a supplier to view details"}
          </div>
          <button
            onClick={onClose}
            className={cn("p-1 rounded hover:opacity-80", theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Tab Content */}
        {selectedRecord ? (
          <Tabs defaultValue="details" className="flex-1 flex flex-col min-h-0 px-3 py-2">
            <SplitViewTabBar tabs={allTabs} />
            
            <div className="flex-1 flex flex-col min-h-0">
              {/* Details Tab */}
              <TabsContent value="details" className="mt-0 flex-1 flex flex-col min-h-0">
                <SupplierDetailsTab
                  selectedRecord={selectedRecord}
                  selectedRecordFiles={selectedRecordFiles}
                  config={config}
                  fields={fields}
                />
              </TabsContent>

              {/* Audit Log Tab */}
              <TabsContent value="audit-log" className="mt-0 flex-1 flex flex-col min-h-0">
                <SupplierAuditLogTab
                  selectedRecord={selectedRecord}
                />
              </TabsContent>
            </div>
          </Tabs>
        ) : (
          <div className={cn("p-4 text-sm", theme.text.secondary)}>
            {labels.noSelectionText || "No supplier selected"}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomSupplierSplitView;
